import React, { useEffect, useState, useCallback } from "react";
import { Users, Plus, KeyRound, Pencil, MapPin, Power } from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, Input, Select, Modal } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { rp, BranchFilter, useBranches } from "../../components/finance";

const STATUS_TONE = { Permanent: "success", Probation: "warn", Contract: "info", Resigned: "danger" };
const EMPTY = { full_name: "", nickname: "", employee_id: "", pin: "", phone: "", email: "", branch_id: "", department: "", position: "Cashier", status: "Probation", base_salary: 0, emergency_contact: "", join_date: "" };

export default function HREmployees() {
  const toast = useToast();
  const { user } = useAuth();
  const canManage = user?.role === "SUPER_ADMIN" || user?.role === "OWNER" || user?.role === "HR_MANAGER";
  const isBranchMgr = user?.role === "BRANCH_MANAGER";
  const branches = useBranches();
  const [branchId, setBranchId] = useState("");
  const [rows, setRows] = useState([]);
  const [config, setConfig] = useState({ positions: [], departments: [], statuses: [] });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {mode, data}
  const [pinModal, setPinModal] = useState(null);
  const [geoModal, setGeoModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get("/hr/employees", { params: { branch_id: branchId || undefined } }),
        api.get("/hr/config"),
      ]);
      setRows(r.data.rows); setConfig(c.data);
    } catch { toast.error("Gagal memuat"); } finally { setLoading(false); }
  }, [branchId, toast]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const m = modal;
    try {
      if (m.mode === "create") await api.post("/hr/employees", m.data);
      else await api.patch(`/hr/employees/${m.data.id}`, m.data);
      toast.success("Tersimpan"); setModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const resetPin = async () => {
    try { await api.post(`/hr/employees/${pinModal.id}/reset-pin`, { pin: pinModal.pin }); toast.success("PIN direset · karyawan wajib ganti PIN saat login berikutnya"); setPinModal(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const toggleLogin = async (e) => {
    const disabling = e.active !== false;
    try {
      await api.post(`/hr/employees/${e.id}/${disabling ? "disable" : "enable"}`);
      toast.success(disabling ? "Login karyawan dinonaktifkan" : "Login karyawan diaktifkan"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
  };

  const set = (k, v) => setModal((s) => ({ ...s, data: { ...s.data, [k]: v } }));

  return (
    <div data-testid="hr-employees-page">
      <PageHeader title="Master Karyawan" subtitle="Data karyawan, posisi & cabang" icon={Users}>
        {!isBranchMgr && <BranchFilter value={branchId} onChange={setBranchId} testid="hr-emp-branch-filter" />}
        {canManage && <Button variant="outline" size="sm" onClick={() => setGeoModal(true)} data-testid="hr-geofence-btn"><MapPin size={14} /> Geofence</Button>}
        {canManage && <Button size="sm" onClick={() => setModal({ mode: "create", data: { ...EMPTY } })} data-testid="hr-add-employee"><Plus size={15} /> Karyawan</Button>}
      </PageHeader>

      {loading ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        rows.length === 0 ? (
          <Card className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-line bg-white/50 px-6 py-12 text-center" data-testid="hr-employees-empty">
            <Users size={40} className="mb-4 text-ink-mute" />
            <h3 className="font-heading text-lg font-bold text-ink">Belum ada karyawan</h3>
            <p className="mt-1 max-w-sm text-sm text-ink-soft">Tambahkan karyawan pertama untuk mengelola absensi, jadwal & penggajian.</p>
            {canManage && <div className="mt-4"><Button onClick={() => setModal({ mode: "create", data: { ...EMPTY } })} data-testid="hr-empty-add-employee"><Plus size={15} /> Tambah Karyawan</Button></div>}
          </Card>
        ) : (
        <Card className="overflow-x-auto" data-testid="hr-employees-table">
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
              <tr><th className="py-2 pr-3">ID</th><th className="py-2 pr-3">Nama</th><th className="py-2 pr-3">Posisi</th><th className="py-2 pr-3">Cabang</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Login</th><th className="py-2 pr-3 text-right">Gaji Pokok</th>{canManage && <th className="py-2 pr-3" />}</tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((e) => (
                <tr key={e.id} data-testid={`hr-emp-row-${e.employee_id}`}>
                  <td className="py-2.5 pr-3 font-mono text-xs text-ink-soft">{e.employee_id}</td>
                  <td className="py-2.5 pr-3 font-medium text-ink">{e.full_name}<span className="block text-xs text-ink-mute">{e.department}</span></td>
                  <td className="py-2.5 pr-3 text-ink-soft">{e.position}</td>
                  <td className="py-2.5 pr-3 text-ink-soft">{e.branch_name || "—"}</td>
                  <td className="py-2.5 pr-3"><Badge tone={STATUS_TONE[e.status]}>{e.status}</Badge></td>
                  <td className="py-2.5 pr-3"><Badge tone={e.active === false ? "danger" : "success"} data-testid={`hr-emp-login-${e.employee_id}`}>{e.active === false ? "Nonaktif" : "Aktif"}</Badge></td>
                  <td className="py-2.5 pr-3 text-right text-ink-soft">{rp(e.base_salary)}</td>
                  {canManage && (
                    <td className="py-2.5 pr-3 text-right">
                      <button onClick={() => setModal({ mode: "edit", data: { ...e } })} className="mr-1 rounded-lg p-1.5 text-ink-mute hover:bg-stone-100" data-testid={`hr-edit-${e.employee_id}`}><Pencil size={15} /></button>
                      <button onClick={() => setPinModal({ id: e.id, pin: "" })} className="mr-1 rounded-lg p-1.5 text-ink-mute hover:bg-stone-100" data-testid={`hr-resetpin-${e.employee_id}`}><KeyRound size={15} /></button>
                      <button onClick={() => toggleLogin(e)} title={e.active === false ? "Aktifkan login" : "Nonaktifkan login"} className={`rounded-lg p-1.5 hover:bg-stone-100 ${e.active === false ? "text-danger-text" : "text-ink-mute"}`} data-testid={`hr-togglelogin-${e.employee_id}`}><Power size={15} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        )
      )}

      {/* Create / Edit modal */}
      <Modal open={!!modal} onClose={() => setModal(null)} title={modal?.mode === "create" ? "Tambah Karyawan" : "Edit Karyawan"} size="lg"
        footer={<><Button variant="outline" onClick={() => setModal(null)}>Batal</Button><Button onClick={save} data-testid="hr-emp-save">Simpan</Button></>}>
        {modal && (
          <div className="grid grid-cols-2 gap-3">
            <Input label="Nama Lengkap" value={modal.data.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="hr-emp-name" />
            <Input label="Nama Panggilan" value={modal.data.nickname || ""} onChange={(e) => set("nickname", e.target.value)} />
            {modal.mode === "create" && <Input label="Employee ID (opsional)" value={modal.data.employee_id} onChange={(e) => set("employee_id", e.target.value)} placeholder="otomatis" />}
            {modal.mode === "create" && <Input label="PIN (4-8 digit)" value={modal.data.pin} onChange={(e) => set("pin", e.target.value.replace(/\D/g, ""))} data-testid="hr-emp-pin" />}
            <Input label="Telepon" value={modal.data.phone || ""} onChange={(e) => set("phone", e.target.value)} />
            <Input label="Email" value={modal.data.email || ""} onChange={(e) => set("email", e.target.value)} />
            {!isBranchMgr && (
              <Select label="Cabang" value={modal.data.branch_id || ""} onChange={(e) => set("branch_id", e.target.value)} data-testid="hr-emp-branch">
                <option value="">— Pilih —</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </Select>
            )}
            <Select label="Departemen" value={modal.data.department || ""} onChange={(e) => set("department", e.target.value)}>
              <option value="">— Pilih —</option>
              {config.departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </Select>
            <Select label="Posisi" value={modal.data.position} onChange={(e) => set("position", e.target.value)} data-testid="hr-emp-position">
              {config.positions.map((p) => <option key={p} value={p}>{p}</option>)}
            </Select>
            <Select label="Status" value={modal.data.status} onChange={(e) => set("status", e.target.value)} data-testid="hr-emp-status">
              {config.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
            <Input label="Tanggal Bergabung" type="date" value={modal.data.join_date || ""} onChange={(e) => set("join_date", e.target.value)} />
            <Input label="Gaji Pokok" type="number" value={modal.data.base_salary || 0} onChange={(e) => set("base_salary", Number(e.target.value))} data-testid="hr-emp-salary" />
            <Input label="Kontak Darurat" value={modal.data.emergency_contact || ""} onChange={(e) => set("emergency_contact", e.target.value)} className="col-span-2" />
          </div>
        )}
      </Modal>

      {/* Reset PIN */}
      <Modal open={!!pinModal} onClose={() => setPinModal(null)} title="Reset PIN" size="sm"
        footer={<><Button variant="outline" onClick={() => setPinModal(null)}>Batal</Button><Button onClick={resetPin} data-testid="hr-resetpin-save">Reset</Button></>}>
        {pinModal && <Input label="PIN Baru (4-8 digit)" value={pinModal.pin} onChange={(e) => setPinModal({ ...pinModal, pin: e.target.value.replace(/\D/g, "") })} data-testid="hr-resetpin-input" />}
      </Modal>

      <GeofenceModal open={geoModal} onClose={() => setGeoModal(false)} />
    </div>
  );
}

function GeofenceModal({ open, onClose }) {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  useEffect(() => { if (open) api.get("/hr/branches").then(({ data }) => setRows(data.rows)).catch(() => {}); }, [open]);
  const save = async (b) => {
    try { await api.patch(`/hr/branches/${b.id}/geofence`, { lat: Number(b.lat), lng: Number(b.lng), radius_m: Number(b.radius_m) }); toast.success("Geofence tersimpan"); }
    catch { toast.error("Gagal"); }
  };
  const set = (id, k, v) => setRows((s) => s.map((b) => b.id === id ? { ...b, [k]: v } : b));
  return (
    <Modal open={open} onClose={onClose} title="Konfigurasi Geofence Cabang" size="lg">
      <p className="mb-3 text-xs text-ink-mute">Atur titik lokasi & radius (meter) untuk validasi GPS absensi.</p>
      <div className="space-y-3" data-testid="hr-geofence-list">
        {rows.map((b) => (
          <div key={b.id} className="rounded-xl border border-line p-3" data-testid={`hr-geofence-${b.id}`}>
            <p className="mb-2 font-semibold text-ink">{b.name}</p>
            <div className="grid grid-cols-4 gap-2">
              <Input label="Lat" type="number" value={b.lat ?? ""} onChange={(e) => set(b.id, "lat", e.target.value)} />
              <Input label="Lng" type="number" value={b.lng ?? ""} onChange={(e) => set(b.id, "lng", e.target.value)} />
              <Input label="Radius (m)" type="number" value={b.radius_m ?? ""} onChange={(e) => set(b.id, "radius_m", e.target.value)} />
              <div className="flex items-end"><Button size="sm" onClick={() => save(b)} data-testid={`hr-geofence-save-${b.id}`}>Simpan</Button></div>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
